import type { TFunction } from 'i18next';
import { z } from 'zod';

import { TERRITORIES } from '~/lib/territories/territories';
import {
  CAMPAIGN_OBJECTIVES,
  CAMPAIGN_TYPES,
  CURRENCY_CODES,
  FREQUENCY_PERIODS,
  MEDIA_PLATFORMS,
} from '~/modules/artist-roster/pages/artist-details/pages/media-planner/constants';

export const getPlanWizardSchema = (t: TFunction<'campaign-planner'>) => {
  // Create enum schemas from constants.
  const CampaignObjectiveSchema = z.enum(CAMPAIGN_OBJECTIVES);
  const CampaignTypeSchema = z.enum(CAMPAIGN_TYPES);
  const MediaPlatformSchema = z.enum(MEDIA_PLATFORMS);
  const CurrencyCodeSchema = z.enum(CURRENCY_CODES);
  const FrequencyPeriodSchema = z.enum(FREQUENCY_PERIODS);

  // TerritoryCode validation - validate against TERRITORIES keys.
  const TerritoryCodeSchema = z.string().refine(
    (code) => code in TERRITORIES,
    (code) => ({
      message: t('digitalCampaigns.planWizard.validation.marketCode', { code }),
    }),
  );

  // FrequencyCap schema.
  const FrequencyCapSchema = z.object({
    times: z.number().min(0, {
      message: t('digitalCampaigns.planWizard.validation.frequencyRequired'),
    }),
    period: FrequencyPeriodSchema,
  });

  // Main schema.
  return z
    .object({
      campaignName: z.string().min(1, {
        message: t(
          'digitalCampaigns.planWizard.validation.campaignNameRequired',
        ),
      }),
      trackId: z.string().optional(),
      objectives: z.array(CampaignObjectiveSchema).min(1, {
        message: t('digitalCampaigns.planWizard.validation.objectivesRequired'),
      }),
      campaignType: CampaignTypeSchema,
      markets: z.array(TerritoryCodeSchema).min(1, {
        message: t('digitalCampaigns.planWizard.validation.marketsRequired'),
      }),
      platforms: z.array(MediaPlatformSchema).min(1, {
        message: t('digitalCampaigns.planWizard.validation.platformsRequired'),
      }),
      startDate: z.coerce.date().refine((date) => !isNaN(date.getTime()), {
        message: t('digitalCampaigns.planWizard.validation.startDateInvalid'),
      }),
      endDate: z.coerce.date().refine((date) => !isNaN(date.getTime()), {
        message: t('digitalCampaigns.planWizard.validation.endDateInvalid'),
      }),
      frequency: FrequencyCapSchema,
      currency: CurrencyCodeSchema,
      totalBudget: z.number().min(0, {
        message: t('digitalCampaigns.planWizard.validation.budgetRequired'),
      }),
    })
    .refine((data) => data.endDate > data.startDate, {
      message: t(
        'digitalCampaigns.planWizard.validation.endDateBeforeStartDate',
      ),
      path: ['endDate'],
    });
};
